import React, { useContext, useEffect, useState } from "react";
import {
  CButton,
  CCard,
  CCardHeader,
  CCol,
  CRow,
  CModal,
  CModalHeader,
  CModalBody,
  CModalFooter,
  CBadge,
  CTooltip,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import DownloadExcel from "src/reusables/DownloadExcel";
import { useLocation, useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import formatter from "src/reusables/formatter";
import { getAll, getAllCategories } from "src/services/Products";
import { Toaster } from "src/components/Toaster";
import ls from "local-storage";
import Authentication, { AUTH_KEY } from "src/utils/auth/Authentication";
import { AuthContext } from "src/App";
import { cilFilter, cilPlus, cilReload, cilX } from "@coreui/icons";
import { usePaginationState } from "src/reusables/usePaginationState";
import ProductFilter from "./Filters";

function Products() {
  const [dataRows, setDataRows] = useState([]);
  const [dataColumns, setDataColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [color, setColor] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState({});
  const [categoryOptions, setCategoryOptions] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  let excelName = "Daftar Produk per " + formatter("dateOnly", new Date());
  const Auth = useContext(AuthContext);

  // Parse URL query parameters
  const getUrlFilters = () => {
    const searchParams = new URLSearchParams(location.search);
    const urlFilters = {};

    // Get all query parameters
    for (const [key, value] of searchParams.entries()) {
      if (value) {
        urlFilters[key] = value;
      }
    }

    return urlFilters;
  };

  // Update URL with current filters
  const updateUrlWithFilters = (newFilters) => {
    const searchParams = new URLSearchParams();

    Object.entries(newFilters).forEach(([key, value]) => {
      if (value) {
        searchParams.set(key, value);
      }
    });

    const newUrl = `${location.pathname}${
      searchParams.toString() ? "?" + searchParams.toString() : ""
    }`;
    navigate(newUrl, { replace: true });
  };

  async function fetchMainData(filtersToUse = filters) {
    try {
      setLoading(true);
      const res = await getAll(filtersToUse);
      const rows =
        res.products &&
        res.products.map((item, i) => {
          return {
            no: i + 1,
            id: item.id,
            sku: item.sku,
            categoryID: item.category_id,
            categoryName: item.categoryName,
            name: item.name,
          };
        });

      setDataRows(rows);

      const columns = [
        { field: "no", headerName: "No", width: "50" },
        { field: "sku", headerName: "SKU", width: "100" },
        { field: "categoryName", headerName: "Kategori", width: "200" },
        { field: "name", headerName: "Nama", width: "300" },
      ];

      setDataColumns(columns);
    } catch (error) {
      setMessage("Gagal memuat data toko");
      setColor("danger");
      setOpen(true);
      console.error("Error fetching stores:", error);
    } finally {
      setLoading(false);
    }
  }

  const [paginationModel, setPaginationModel] = usePaginationState();

  // Initialize filters from URL and fetch data
  useEffect(() => {
    const urlFilters = getUrlFilters();
    setFilters(urlFilters);
    fetchMainData(urlFilters);
  }, [location.search]); // This will run on mount AND when URL changes

  useEffect(() => {
    if (location.state?.page !== undefined) {
      setPaginationModel({
        page: location.state.page,
        pageSize: location.state.pageSize || paginationModel.pageSize,
      });
    }
  }, [location.state]);

  useEffect(() => {
    const fetchCategoryOptions = async () => {
      try {
        const response = await getAllCategories();
        const catData = response?.categories || response || [];
        const options = catData.map((cat) => ({
          value: cat.id,
          label: `${cat.name}`,
        }));
        setCategoryOptions(options);
      } catch (error) {
        console.error("Error fetching Category options:", error);
        setCategoryOptions([]);
      }
    };

    fetchCategoryOptions();
  }, []);

  function handleRowClick(params) {
    navigate(`/product/details/${params.row.id}`, {
      state: {
        fromPage: paginationModel.page,
        fromPageSize: paginationModel.pageSize,
      },
    });
  }

  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
    updateUrlWithFilters(newFilters);
    setFilterOpen(false);
    fetchMainData(newFilters);
  };
  // Helper functions for filter display
  const getFilterLabel = (key) => {
    const labels = {
      name: "Nama",
      sku: "SKU",
      categoryId: "Kategori",
    };
    return labels[key] || key;
  };

  const getFilterValueDisplay = (key, v) => {
    if (key === "categoryId") {
      const categoryOpt = categoryOptions.find((option) => option.value == v);
      return categoryOpt ? categoryOpt.label : v;
    }

    return v;
  };

  // Filter handlers
  const handleRemoveFilter = (key) => {
    const newFilters = { ...filters, [key]: "" };
    setFilters(newFilters);
    updateUrlWithFilters(newFilters);
    fetchMainData(newFilters);
  };

  const handleResetFilters = () => {
    const emptyFilters = {
      name: "",
      sku: "",
      categoryId: "",
    };
    setFilters(emptyFilters);
    updateUrlWithFilters(emptyFilters);
    fetchMainData(emptyFilters);
  };

  return (
    <div style={{ color: "black" }}>
      <Toaster
        open={open}
        message={message}
        setOpen={setOpen}
        ahd={1500}
        color={color ?? "red"}
      />

      <CCard>
        <CCardHeader className="p-3 p-md-4">
          <div className="d-flex flex-column gap-3">
            <CRow className="align-items-center g-3">
              <CCol xs={12} md={6} className="pe-md-3">
                <h4 className="mb-0"> Daftar Produk </h4>
              </CCol>

              <CCol xs={12} md={6} className="ps-md-3">
                <div className="d-flex flex-wrap justify-content-md-end gap-2">
                  <CTooltip content="Refresh Data">
                    <CButton
                      color="light"
                      onClick={fetchMainData}
                      disabled={loading}
                      className="p-2"
                      shape="pill"
                    >
                      <CIcon icon={cilReload} />
                    </CButton>
                  </CTooltip>

                  <CTooltip content="Filter Data">
                    <CButton
                      color="light"
                      onClick={() => setFilterOpen(true)}
                      className="p-2"
                      shape="pill"
                    >
                      <CIcon icon={cilFilter} />
                    </CButton>
                  </CTooltip>

                  <CButton
                    color="primary"
                    onClick={() => navigate("/product/actions/")}
                    className="px-3"
                  >
                    <CIcon icon={cilPlus} className="me-2" />
                    Tambah Toko
                  </CButton>

                  <DownloadExcel
                    rows={dataRows}
                    columns={dataColumns.filter(
                      (col) => col.field !== "actions"
                    )}
                    excelName={excelName}
                    className="ms-2"
                  />
                </div>
              </CCol>
            </CRow>
            {/* Filter Chips Section */}
            {Object.keys(filters).some((key) => filters[key]) && (
              <div className="d-flex flex-wrap align-items-center gap-2">
                <small className="text-muted me-2">Filter Aktif:</small>
                {Object.entries(filters).map(
                  ([key, value]) =>
                    value && (
                      <CBadge
                        key={key}
                        color="primary"
                        className="d-flex align-items-center py-2 px-3"
                        shape="rounded-pill"
                      >
                        <span className="me-2">
                          {getFilterLabel(key)}:{" "}
                          {getFilterValueDisplay(key, value)}
                        </span>
                        <div
                          role="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveFilter(key);
                          }}
                          className="d-flex align-items-center"
                        >
                          <CIcon icon={cilX} width={14} />
                        </div>
                      </CBadge>
                    )
                )}
                <CButton
                  color="link"
                  size="sm"
                  className="text-decoration-none p-0 ms-2"
                  onClick={handleResetFilters}
                >
                  Clear All
                </CButton>
              </div>
            )}
          </div>
        </CCardHeader>
        <div style={{ margin: "16px" }}>
          <DataGrid
            rows={dataRows}
            columns={dataColumns}
            onRowClick={handleRowClick}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[5, 10, 20, 25]}
            disableRowSelectionOnClick
            autoHeight
            sx={{
              "& .MuiDataGrid-cell:focus": {
                outline: "none",
              },
              "& .MuiDataGrid-columnHeader:focus": {
                outline: "none",
              },
            }}
          />
        </div>
      </CCard>
      <ProductFilter
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        onApply={handleApplyFilters}
        initialFilters={filters}
      />
    </div>
  );
}

export default Products;
